import React from 'react'
import Link from 'next/link'
import type { CityLinksData } from '@src/content/cities'
import classes from '@src/styles/cityLinks.module.css'

export type CityLinksProps = {
  /**
   * From `getCityLinks(serviceSlug)` in `getStaticProps`. Null for a service with
   * no city pages, which renders nothing.
   */
  data: CityLinksData | null
}

/**
 * The "by city" block on a national service page — the only inbound links the
 * city pages get, and the ones that matter most: they come from the strongest
 * page on the topic and land on the weakest.
 *
 * Takes its data as a prop rather than reading `CITY_SERVICES` itself, so the
 * city content set never reaches the browser — see `getCityLinks`. The list is
 * built from the registry, so it cannot link to a city page that was not built,
 * and adding a city to the registry adds it here with no edit.
 *
 * Not rendered on the city pages themselves: those carry a compact list of
 * sibling city names instead (see CityServicePage), so every city page links to
 * every other one without a block of other cities' summaries diluting its topic.
 */
function CityLinks({ data }: CityLinksProps) {
  if (!data || !data.cities.length) return null

  return (
    <section className={classes.container}>
      <h2 className={classes.heading}>{data.label} by city</h2>
      <p className={classes.intro}>
        Rules, permits and the agency you answer to change at the county line. These pages cover
        what {data.label.toLowerCase()} actually involves where you operate.
      </p>
      <ul className={classes.grid}>
        {data.cities.map((city) => (
          <li key={city.slug}>
            <Link href={`/${data.serviceSlug}/${city.slug}`} className={classes.card}>
              <span className={classes.cardTitle}>
                {city.cityName}, {city.stateCode}
              </span>
              <span className={classes.cardDescription}>{city.linkSummary}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

export default CityLinks
